import { Fragment } from 'react';
import { Link, useMatches } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHomeTarget } from '@/hooks/use-home-target';

// Breadcrumbs derivados del array de matches del router.
// Cada route puede declarar `handle: { breadcrumb: 'Productos' }`. Las routes
// sin breadcrumb se ignoran. Esto permite definir crumbs por route sin tener
// que centralizarlos en un mapa global.

interface RouteHandle {
  breadcrumb?: string;
}

interface Crumb {
  label: string;
  to: string;
}

// Extrae los crumbs visibles del array de matches.
// El primer crumb es "Inicio" -> "/" para anchorar la jerarquía, salvo con
// `showHome` en false: sin tarjetas en el dashboard `/` redirige a la primera
// sección (ver `useHomeTarget`) y el crumb linkearía a esa redirección.
function extractCrumbs(
  matches: ReturnType<typeof useMatches>,
  showHome: boolean,
): readonly Crumb[] {
  const crumbs: Crumb[] = showHome ? [{ label: 'Inicio', to: '/' }] : [];
  for (const match of matches) {
    const handle = match.handle as RouteHandle | undefined;
    if (!handle?.breadcrumb) continue;
    // Evita duplicar el "Inicio" si la route raíz también declara breadcrumb.
    if (match.pathname === '/') continue;
    crumbs.push({ label: handle.breadcrumb, to: match.pathname });
  }
  return crumbs;
}

interface BreadcrumbsProps {
  className?: string;
}

export const Breadcrumbs = ({ className }: BreadcrumbsProps) => {
  const matches = useMatches();
  const home = useHomeTarget();
  const crumbs = extractCrumbs(matches, home.hasDashboard);

  return (
    // Van arriba del contenido (`AppShell`), que en el celular no las muestra.
    // Si igual se pintan angostas, queda solo la página actual, con "…" si
    // tampoco entra; los niveles anteriores aparecen desde `sm`. `min-w-0` en
    // cada nivel es lo que deja que el texto se acorte en vez de cortarse en
    // seco.
    <nav aria-label="Migas de pan" className={cn('flex min-w-0 items-center', className)}>
      <ol className="text-text-muted flex min-w-0 items-center gap-1.5 text-sm">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <Fragment key={`${crumb.to}-${index}`}>
              <li className={cn('items-center', isLast ? 'flex min-w-0' : 'hidden sm:flex')}>
                {isLast ? (
                  <span aria-current="page" className="text-text truncate font-medium">
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.to}
                    className="hover:text-text focus-visible:ring-ring rounded-sm transition-colors focus-visible:ring-2 focus-visible:outline-hidden"
                  >
                    {crumb.label}
                  </Link>
                )}
              </li>
              {!isLast ? (
                <li aria-hidden="true" className="hidden items-center sm:flex">
                  <ChevronRight className="h-3.5 w-3.5" />
                </li>
              ) : null}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};
